import React, { useEffect, useMemo, useState } from "react";
import * as Scrivito from "scrivito";
import cx from "classnames";
import {
  getHomepage,
  getLeftSidebarButtonByPageType,
  isSystemConfiguratorLayout,
} from "utils";
import {
  LEFT_SIDEBAR_BUTTONS_ARRAY_CONF,
  LEFT_SIDEBAR_BUTTONS_ARRAY_SAME,
  SUPPORT_BTN,
} from "variables";
import { usePageType } from "hooks";

import { LeftSidebarButton } from "./LeftSidebarButton";
import { LeftSidebarSubnav } from "./LeftSidebarSubnav";
import s from "./LeftSidebar.module.scss";
import { useIsSameProj } from "stores/globalStore";
import type { LeftSidebarAcitveButton } from "interfaces/shared.types";
import { useFeatureFlags } from "stores/user";

type LeftSidebarProps = {
  activeButton: LeftSidebarAcitveButton;
  setActiveButton: React.Dispatch<
    React.SetStateAction<null | LeftSidebarAcitveButton>
  >;
};

export const LeftSidebar = Scrivito.connect(
  ({ activeButton, setActiveButton }: LeftSidebarProps) => {
    const isSameProj = useIsSameProj();
    Scrivito.currentPage()?.path();
    const featureFlags = useFeatureFlags();

    const linkList = useMemo(() => {
      const list = isSameProj
        ? LEFT_SIDEBAR_BUTTONS_ARRAY_SAME
        : LEFT_SIDEBAR_BUTTONS_ARRAY_CONF;

      return list.filter(
        (btn) => !btn.FEATURE_FLAG || featureFlags?.[btn.FEATURE_FLAG],
      );
    }, [featureFlags, isSameProj]);

    const refsObj = useMemo(() => {
      const obj: Record<string, Scrivito.Obj[]> = {};
      linkList.forEach((btn) => {
        obj[`${btn.title}_refs`] = [];
      });
      return obj;
    }, [linkList]);

    const [currentPageButton, setCurrentPageButton] =
      useState<LeftSidebarAcitveButton | null>(null);
    const [refs, setRefs] = useState<Record<string, Scrivito.Obj[]>>(refsObj);
    const pageType = usePageType();

    useEffect(() => {
      const getNavRefs = async () => {
        const homepage = await Scrivito.load(() => getHomepage());
        const btnRefs = await Scrivito.load(() => {
          if (!homepage) return [];
          return linkList.map((btn) => ({
            btnTitle: btn.title,
            btnRefs: homepage.get(btn.refProp),
          }));
        });
        if (!btnRefs.length) return;
        const newRefsObj: Record<string, Scrivito.Obj[]> = {};
        btnRefs.forEach((btn) => {
          const btnRefs = btn.btnRefs;
          if (Array.isArray(btnRefs)) {
            newRefsObj[`${btn.btnTitle}_refs`] = btnRefs as Scrivito.Obj[];
          }
        });
        setRefs(newRefsObj);
      };
      getNavRefs();

      // to reset active button when we are leaving pages with LeftSidebar
      return () => setActiveButton(null);
    }, [linkList, setActiveButton]);

    useEffect(() => {
      if (!pageType) {
        return;
      }
      const button = getLeftSidebarButtonByPageType(pageType);
      if (!button) {
        return;
      }

      setCurrentPageButton(button);
    }, [pageType]);

    const isLeftSidebarVisible =
      pageType && isSystemConfiguratorLayout(pageType);
    if (!isLeftSidebarVisible) {
      return null;
    }
    return (
      <div
        className={cx(
          s.leftSidebarWrapper,
          "d-flex flex-shrink-0 w-100 bg-gray-700 z-index-1",
          {
            [s.leftSidebarWrapperExtended]:
              activeButton?.subNavLevel?.value ?? 0 > 0,
          },
        )}
      >
        <div
          className={cx(
            s.leftSidebar,
            "d-flex flex-column justify-content-between",
          )}
        >
          <div>
            {linkList.map((item) => (
              <LeftSidebarButton
                key={item.title}
                item={item}
                currentPageButton={currentPageButton}
                activeButton={activeButton}
                setActiveButton={setActiveButton}
              />
            ))}
          </div>
          {/* TODO: forthcoming feature (hidden acc US-19953) */}
          <LeftSidebarButton
            key="support_button"
            item={SUPPORT_BTN}
            currentPageButton={currentPageButton}
            activeButton={activeButton}
            setActiveButton={setActiveButton}
          />
        </div>
        {linkList.map((btn) => (
          <LeftSidebarSubnav
            key={btn.title}
            activeButton={activeButton}
            btn={btn}
            references={refs[`${btn.title}_refs`] as Scrivito.Obj[]}
            setActiveButton={setActiveButton}
          />
        ))}
      </div>
    );
  },
);
